import {
  Box,
  styled,
  Typography
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { getHours, getMinutes } from "date-fns";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import { ClockIcon, GraduationCap, MapPin, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SessionWithTeachersAndTutors } from "../../types.d";
interface SessionItemProps {
  index: number;
  totalSessions: number;
  session: SessionWithTeachersAndTutors;
  isVirtual: boolean;
  theme: Theme;
  t: TFunction;
}

export function SessionItem({ index, totalSessions, session, isVirtual, theme, t }: SessionItemProps) {
  const hasTeachersOrTutors = (session.teachers && session.teachers.length > 0) || (session.tutors && session.tutors.length > 0);
  const { i18n } = useTranslation();

  const formatSessionDateTime = (startDate: string | number, endDate: string | number): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const startDay = dayjs(start).locale(i18n.language).format("DD");
    const startMonth = dayjs(start).locale(i18n.language).format("MMMM");
    const startYear = dayjs(start).locale(i18n.language).format("YYYY");
    const formattedStartDate = `${startDay} ${startMonth.charAt(0).toUpperCase() + startMonth.slice(1)} ${startYear}`;
    
    const endDay = dayjs(end).locale(i18n.language).format("DD");
    const endMonth = dayjs(end).locale(i18n.language).format("MMMM");
    const endYear = dayjs(end).locale(i18n.language).format("YYYY");
    const formattedEndDate = `${endDay} ${endMonth.charAt(0).toUpperCase() + endMonth.slice(1)} ${endYear}`;
 
    const startTime = `${getHours(start) < 10 ? `0${getHours(start)}` : getHours(start)}:${getMinutes(start) < 10 ? `0${getMinutes(start)}` : getMinutes(start)}`;
    const endTime = `${getHours(end) < 10 ? `0${getHours(end)}` : getHours(end)}:${getMinutes(end) < 10 ? `0${getMinutes(end)}` : getMinutes(end)}`;
 
    const dateRange = formattedStartDate === formattedEndDate 
      ? formattedStartDate 
      : `${formattedStartDate} - ${formattedEndDate}`;
    
    return `${dateRange} | ${startTime} - ${endTime}`;
  };

  const getLocationText = (
    isVirtual: boolean,
    location: string | undefined,
    rooms: string[] | undefined,
    t: TFunction
  ): string => {
    if (isVirtual) {
      return t("virtual_location");
    }
    const locationText = location || t("physical_location");
    const roomsText = rooms && rooms.length > 0 ? rooms.join(", ") : "";
    return roomsText ? `${locationText}\n${roomsText}` : locationText;
  };

  return (
    <Box
      sx={ { 
        padding: index !== totalSessions -1 ? "0.5rem" : "1.5rem 0rem", paddingBottom: "0rem", 
        color: theme.customColors.exploreCardText 
      } }
    >
      <Box sx={ { alignItems: "center", display: "flex", mb: 0.5 } }>
        <ClockIcon
          size="1rem"
        />
        <StyledTypography 
          variant="body2" 
          fontSize="1rem"
          fontWeight={ 700 }
        >
          { formatSessionDateTime(session.startDate, session.endDate) }
        </StyledTypography>
      </Box>
      
      <Box sx={ { alignItems: "center", display: "flex" } }>
        { isVirtual ? (
          <Video  size="1rem"/>
        ) : (
          <MapPin  size="1rem"/>
        ) }
        <StyledTypography 
          variant="body2" 
          sx={ {
            whiteSpace: "pre-line"
          } }
        >
          { getLocationText(isVirtual, session.location, session.rooms, t) }
        </StyledTypography>
      </Box>
      
      { hasTeachersOrTutors && (
        <Box sx={ { alignItems: "flex-start", display: "flex", flexWrap: "wrap", gap: 1, mt: "0.75rem" } }>
          { session.teachers?.map((teacher, idx) => (
            <Box key={ `teacher-${idx}` } sx={ { alignItems: "center", display: "flex", mr: 1 } }>
              <GraduationCap size="1rem"/>
              <StyledTypography 
                variant="body2" 
              >
                { teacher.completeName } ({ t("teacher_field").toLocaleLowerCase() })
              </StyledTypography>
            </Box>
          )) }
          { session.tutors?.map((tutor, idx) => (
            <Box key={ `tutor-${idx}` } sx={ { alignItems: "center", display: "flex" } }>
              <GraduationCap size="1rem"/>
              <StyledTypography
                variant="body2" 
              >
                { tutor.completeName } (tutor)
              </StyledTypography>
            </Box>
          )) }
        </Box>
      ) }
    </Box>
  );
}


const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  marginLeft: "0.5rem",
  fontSize: "0.75rem" 
}));
